/**
 * Маршрутизация: карточка дела либо раздел администрирования.
 *
 * Маршрутизатор не подключается сознательно: разделов два, а приложение
 * живёт в iframe карточки — адресная строка ему не принадлежит.
 */

import { useEffect, useState } from 'react';
import { CasePage } from './features/case/CasePage.js';
import { DictionariesPage } from './features/admin/DictionariesPage.js';
import { TemplatesPage } from './features/admin/TemplatesPage.js';
import { AccessPage } from './features/admin/AccessPage.js';
import { api, type Me } from './api.js';
import { initBx24, readCaseContext, type CaseContext } from './bx24.js';
import { useEntityType } from './storage/cases.js';
import { resolveFieldNames } from './storage/diagnostics.js';

type Screen = 'case' | 'dictionaries' | 'templates' | 'access';

export function App() {
  const [context, setContext] = useState<CaseContext | null>(null);
  const [screen, setScreen] = useState<Screen>('case');
  const [me, setMe] = useState<Me | null>(null);

  useEffect(() => {
    void initBx24().then(async () => {
      const ctx = readCaseContext();
      useEntityType(ctx.entityTypeId);

      // Открыто ли приложение этому сотруднику. Сбой ответа не запирает
      // карточку: права всё равно проверяет сервер на каждом запросе
      const who = await api.me().catch(() => null);
      setMe(who);
      if (who && !who.hasAccess) {
        setContext(ctx);
        return;
      }

      // Коды полей сделки присваивает портал: пока они не найдены по названию,
      // профиль писался бы в несуществующее поле, и портал принял бы это молча
      if (!ctx.standalone) await resolveFieldNames(ctx.entityTypeId).catch(() => undefined);

      setContext(ctx);
      // Вне карточки дела приложение открывается сразу на администрировании
      if (ctx.standalone) setScreen('dictionaries');
    });
  }, []);

  if (!context) return <div className="loading">Подключение к порталу…</div>;

  // Закрытая установка: сотрудник, которому доступ не открыт, дальше не проходит
  if (me && !me.hasAccess) {
    return (
      <div className="access-denied">
        <h1>Доступ только для администраторов портала</h1>
        <p>Чтобы работать с приложением, попросите администратора портала открыть вам доступ.</p>
      </div>
    );
  }

  const manageAccess = me?.accessMode === 'admins' && me.isPortalAdmin;

  return (
    <div className="app">
      {(context.standalone || screen !== 'case') && (
        <nav className="app-nav">
          {!context.standalone && (
            <button type="button" className={screen === 'case' ? 'active' : ''} onClick={() => setScreen('case')}>
              Карточка дела
            </button>
          )}
          <button type="button" className={screen === 'dictionaries' ? 'active' : ''} onClick={() => setScreen('dictionaries')}>
            Справочники
          </button>
          <button type="button" className={screen === 'templates' ? 'active' : ''} onClick={() => setScreen('templates')}>
            Шаблоны
          </button>
          {manageAccess && (
            <button type="button" className={screen === 'access' ? 'active' : ''} onClick={() => setScreen('access')}>
              Доступ
            </button>
          )}
        </nav>
      )}

      {screen === 'case' && !context.standalone && <CasePage context={context} />}
      {screen === 'case' && context.standalone && (
        <div className="block">
          <h2>Приложение открыто вне карточки дела</h2>
          <p>Откройте сделку и перейдите на вкладку «Документы по банкротству».</p>
        </div>
      )}
      {screen === 'dictionaries' && <DictionariesPage />}
      {screen === 'templates' && <TemplatesPage />}
      {screen === 'access' && manageAccess && <AccessPage />}

      {!context.standalone && screen === 'case' && (
        <footer className="app-footer">
          <button type="button" className="link-button" onClick={() => setScreen('dictionaries')}>
            Справочники
          </button>
          <button type="button" className="link-button" onClick={() => setScreen('templates')}>
            Шаблоны
          </button>
          {manageAccess && (
            <button type="button" className="link-button" onClick={() => setScreen('access')}>
              Доступ
            </button>
          )}
        </footer>
      )}
    </div>
  );
}
